/**
 * SWOT Analysis Section Component
 * Comprehensive SWOT analysis with risk assessment matrix
 */

import React, { useState, useEffect } from 'react';
import { 
  PlusIcon, 
  EditIcon, 
  TrashIcon 
} from 'lucide-react';
import type { KATABoardV2 } from '../../../types/kataBoardV2';
import { SWOTService, type SWOTItem, type SWOTItemCreate } from '../../../services/swot';
import { useKataBoardCanEdit } from '../../../contexts/KataBoardAccessContext';

interface SWOTAnalysisSectionProps {
  kataBoard: KATABoardV2;
  isEditing?: boolean; // Make optional since SWOT is always editable
  onUpdate: () => void;
}

// Using SWOTItem from service

const SWOTAnalysisSection: React.FC<SWOTAnalysisSectionProps> = ({ kataBoard, isEditing, onUpdate }) => {
  const canEdit = useKataBoardCanEdit();
  const [swotItems, setSWOTItems] = useState<SWOTItem[]>([]);
  const [activeTab, setActiveTab] = useState<'strengths' | 'weaknesses' | 'opportunities' | 'threats' | 'risk-matrix'>('strengths');
  const [editingItem, setEditingItem] = useState<SWOTItem | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [newItem, setNewItem] = useState<SWOTItemCreate>({
    kata_board: kataBoard.id,
    swot_type: 'strength',
    aspect: '',
    description: '',
    responsible: '',
    date: '',
    effectiveness: ''
  });

  // Load SWOT items from API
  useEffect(() => {
    loadSWOTItems();
  }, [kataBoard.id]);

  const loadSWOTItems = async () => {
    try {
      setLoading(true);
      setError(null);
      const items = await SWOTService.getByKataBoard(kataBoard.id);
      console.log('Loaded SWOT items:', items);
      setSWOTItems(items);
    } catch (err) {
      console.error('Failed to load SWOT items:', err);
      setError('Failed to load SWOT analysis data');
    } finally {
      setLoading(false);
    }
  };

  const getSectionColor = (type: string) => {
    switch (type) {
      case 'strengths': return { bg: 'bg-green-100', border: 'border-green-400', text: 'text-green-800', header: 'bg-green-500' };
      case 'weaknesses': return { bg: 'bg-yellow-100', border: 'border-yellow-400', text: 'text-yellow-800', header: 'bg-yellow-500' };
      case 'opportunities': return { bg: 'bg-blue-100', border: 'border-blue-400', text: 'text-blue-800', header: 'bg-blue-500' };
      case 'threats': return { bg: 'bg-red-100', border: 'border-red-400', text: 'text-red-800', header: 'bg-red-500' };
      default: return { bg: 'bg-gray-100', border: 'border-gray-400', text: 'text-gray-800', header: 'bg-gray-500' };
    }
  };

  const handleAddItem = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const itemToCreate: SWOTItemCreate = {
        ...newItem,
        kata_board: kataBoard.id,
        swot_type: activeTab === 'strengths' ? 'strength' : 
                   activeTab === 'weaknesses' ? 'weakness' :
                   activeTab === 'opportunities' ? 'opportunity' : 'threat'
      };

      // Validate the item
      const validation = SWOTService.validateSWOTItem(itemToCreate);
      if (!validation.isValid) {
        setError(validation.errors.join(', '));
        return;
      }

      const createdItem = await SWOTService.create(itemToCreate);
      console.log('Created SWOT item:', createdItem);
      
      // Refresh the list to get items with proper IDs
      await loadSWOTItems();
      
      // Reset form
      setNewItem({
        kata_board: kataBoard.id,
        swot_type: 'strength',
        aspect: '',
        description: '',
        responsible: '',
        date: '',
        effectiveness: ''
      });
      setShowAddForm(false);
      
    } catch (err) {
      console.error('Failed to create SWOT item:', err);
      setError('Failed to create SWOT item');
    } finally {
      setLoading(false);
    }
  };

  const getRiskLevel = (probability: number, impact: number) => {
    return SWOTService.calculateRiskLevel(probability, impact);
  };

  const handleDeleteItem = async (item: SWOTItem) => {
    console.log('handleDeleteItem called with:', item);
    console.log('Item ID:', item.id);
    if (!item.id) {
      console.log('No ID found, cannot delete');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      console.log('Attempting to delete SWOT item with ID:', item.id);
      await SWOTService.delete(item.id);
      console.log('SWOT item deleted successfully');
      // Update state with functional update to ensure proper re-render
      setSWOTItems(prevItems => prevItems.filter(i => i.id !== item.id));
      
    } catch (err) {
      console.error('Failed to delete SWOT item:', err);
      console.error('Error details:', (err as any).response?.data || (err as any).message);
      setError('Failed to delete SWOT item');
    } finally {
      setLoading(false);
    }
  };

  const handleEditItem = async (updatedItem: SWOTItem) => {
    console.log('handleEditItem called with:', updatedItem);
    console.log('Item ID:', updatedItem.id);
    if (!updatedItem.id) {
      console.log('No ID found, cannot edit');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const itemToUpdate: Partial<SWOTItemCreate> = {
        aspect: updatedItem.aspect,
        description: updatedItem.description,
        responsible: updatedItem.responsible,
        date: updatedItem.date,
        effectiveness: updatedItem.effectiveness,
        probability: updatedItem.probability,
        impact: updatedItem.impact,
        cause_analysis: updatedItem.cause_analysis,
        actions: updatedItem.actions,
        order: updatedItem.order
      };

      console.log('Attempting to update SWOT item with ID:', updatedItem.id);
      console.log('Update data:', itemToUpdate);
      const updated = await SWOTService.update(updatedItem.id, itemToUpdate);
      console.log('SWOT item updated successfully:', updated);
      // Update state with functional update to ensure proper re-render
      setSWOTItems(prevItems => prevItems.map(item => item.id === updated.id ? updated : item));
      setEditingItem(null);
      setShowAddForm(false);
      
    } catch (err) {
      console.error('Failed to update SWOT item:', err);
      console.error('Error details:', (err as any).response?.data || (err as any).message);
      setError('Failed to update SWOT item');
    } finally {
      setLoading(false);
    }
  };

  const renderSWOTTable = (type: 'strengths' | 'weaknesses' | 'opportunities' | 'threats') => {
    const colors = getSectionColor(type);
    const items = swotItems.filter(item => 
      (type === 'strengths' && item.swot_type === 'strength') ||
      (type === 'weaknesses' && item.swot_type === 'weakness') ||
      (type === 'opportunities' && item.swot_type === 'opportunity') ||
      (type === 'threats' && item.swot_type === 'threat')
    );

    const getTitle = () => {
      switch (type) {
        case 'strengths': return 'Strenghts (Forces)';
        case 'weaknesses': return 'Weakness (Faiblesses)';
        case 'opportunities': return 'Opportunities';
        case 'threats': return 'Threats (Menaces)';
      }
    };

    return (
      <div className={`border-2 ${colors.border} rounded-lg overflow-hidden`}>
        {/* Header */}
        <div className={`${colors.header} text-white p-4 flex items-center justify-between`}>
          <h3 className="text-xl font-bold">{getTitle()}</h3>
          <div className="text-white opacity-80">
            {/* Add decorative icon based on type */}
            {type === 'strengths' && '💪'}
            {type === 'weaknesses' && '⚠️'}
            {type === 'opportunities' && '🌟'}
            {type === 'threats' && '⚡'}
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className={`${colors.bg}`}>
              <tr>
                <th className="border border-gray-300 px-4 py-2 text-left font-semibold">Aspect</th>
                {type === 'strengths' && (
                  <>
                    <th className="border border-gray-300 px-4 py-2 text-left font-semibold">Actions de Maintien</th>
                    <th className="border border-gray-300 px-4 py-2 text-left font-semibold">Responsable</th>
                    <th className="border border-gray-300 px-4 py-2 text-left font-semibold">Date</th>
                    <th className="border border-gray-300 px-4 py-2 text-left font-semibold">Effectiveness of the action</th>
                  </>
                )}
                {type === 'weaknesses' && (
                  <>
                    <th className="border border-gray-300 px-4 py-2 text-left font-semibold">Analyse des Causes</th>
                    <th className="border border-gray-300 px-4 py-2 text-left font-semibold">Actions</th>
                    <th className="border border-gray-300 px-4 py-2 text-left font-semibold">Responsable</th>
                    <th className="border border-gray-300 px-4 py-2 text-left font-semibold">Date</th>
                    <th className="border border-gray-300 px-4 py-2 text-left font-semibold">Effectiveness of the action</th>
                  </>
                )}
                {type === 'opportunities' && (
                  <>
                    <th className="border border-gray-300 px-4 py-2 text-left font-semibold">Actions</th>
                    <th className="border border-gray-300 px-4 py-2 text-left font-semibold">Responsable</th>
                    <th className="border border-gray-300 px-4 py-2 text-left font-semibold">Date</th>
                    <th className="border border-gray-300 px-4 py-2 text-left font-semibold">Effectiveness of the action</th>
                  </>
                )}
                {type === 'threats' && (
                  <>
                    <th className="border border-gray-300 px-4 py-2 text-left font-semibold">Probabilité</th>
                    <th className="border border-gray-300 px-4 py-2 text-left font-semibold">Impact</th>
                    <th className="border border-gray-300 px-4 py-2 text-left font-semibold">Valeur attendue</th>
                    <th className="border border-gray-300 px-4 py-2 text-left font-semibold">Évaluation de risque</th>
                    <th className="border border-gray-300 px-4 py-2 text-left font-semibold">Analyse des Causes</th>
                    <th className="border border-gray-300 px-4 py-2 text-left font-semibold">Actions</th>
                    <th className="border border-gray-300 px-4 py-2 text-left font-semibold">Responsable</th>
                    <th className="border border-gray-300 px-4 py-2 text-left font-semibold">Date</th>
                    <th className="border border-gray-300 px-4 py-2 text-left font-semibold">Effectiveness of the action</th>
                  </>
                )}
                {canEdit && (
                <th className="border border-gray-300 px-4 py-2 text-center font-semibold">Actions</th>
                )}
              </tr>
            </thead>
            <tbody>
              {items.map((item, index) => (
                <tr key={item.id || index} className="hover:bg-gray-50">
                  <td className="border border-gray-300 px-4 py-2">{item.aspect}</td>
                  {type === 'threats' && (
                    <>
                      <td className="border border-gray-300 px-4 py-2 text-center">{item.probability || ''}</td>
                      <td className="border border-gray-300 px-4 py-2 text-center">{item.impact || ''}</td>
                      <td className="border border-gray-300 px-4 py-2 text-center">{item.risk_value || ''}</td>
                      <td className="border border-gray-300 px-4 py-2 text-center">
                        {item.probability && item.impact && (
                          <span className={`px-2 py-1 rounded text-sm ${SWOTService.getRiskColorClass(getRiskLevel(item.probability, item.impact).level)}`}>
                            {getRiskLevel(item.probability, item.impact).display}
                          </span>
                        )}
                      </td>
                      <td className="border border-gray-300 px-4 py-2">{item.cause_analysis || ''}</td>
                      <td className="border border-gray-300 px-4 py-2">{item.actions || ''}</td>
                    </>
                  )}
                  {type !== 'threats' && type === 'weaknesses' && (
                    <td className="border border-gray-300 px-4 py-2">{item.cause_analysis || ''}</td>
                  )}
                  {type !== 'threats' && (
                    <td className="border border-gray-300 px-4 py-2">{item.description}</td>
                  )}
                  <td className="border border-gray-300 px-4 py-2">{item.responsible || ''}</td>
                  <td className="border border-gray-300 px-4 py-2">{item.date || ''}</td>
                  <td className="border border-gray-300 px-4 py-2">{item.effectiveness || ''}</td>
                  {canEdit && (
                  <td className="border border-gray-300 px-4 py-2 text-center">
                    <button
                      onClick={() => {
                        console.log('Edit button clicked for item:', item);
                        setEditingItem(item);
                        setShowAddForm(true);
                      }}
                      className="text-blue-500 hover:text-blue-700 mr-2"
                    >
                      <EditIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => {
                        console.log('Delete button clicked for item:', item);
                        handleDeleteItem(item);
                      }}
                      className="text-red-500 hover:text-red-700"
                      disabled={loading}
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </td>
                  )}
                </tr>
              ))}
              {items.length === 0 && (
                <tr>
                  <td colSpan={type === 'threats' ? 10 : 6} className="border border-gray-300 px-4 py-8 text-center text-gray-500">
                    No {type} defined yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Add Button */}
        {canEdit && (
        <div className="p-4 border-t border-gray-200">
          <button
            onClick={() => {
              setActiveTab(type);
              setShowAddForm(true);
            }}
            className={`inline-flex items-center px-4 py-2 ${colors.header} text-white text-sm font-medium rounded-md hover:opacity-90`}
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Add {type.slice(0, -1)}
          </button>
        </div>
        )}
      </div>
    );
  };

  const renderRiskMatrix = () => {
    const threats = swotItems.filter(item => item.swot_type === 'threat' && item.probability && item.impact);
    
    return (
      <div className="bg-white border-2 border-gray-300 rounded-lg overflow-hidden">
        <div className="bg-gray-500 text-white p-4">
          <h3 className="text-xl font-bold">Risk Evaluation Matrix</h3>
        </div>
        
        <div className="p-6">
          <div className="grid grid-cols-2 gap-6">
            {/* Risk Values Table */}
            <div>
              <h4 className="font-semibold mb-3">Valeur attendue (= Probabilité x Impact)</h4>
              <div className="border border-gray-300">
                <div className="grid grid-cols-2">
                  <div className="border-r border-gray-300 p-2 font-semibold bg-gray-100">Values</div>
                  <div className="p-2 font-semibold bg-gray-100">Risk Level</div>
                </div>
                {[1,2,3,4,5,6,8,9,10,12,15,16].map(val => (
                  <div key={val} className="grid grid-cols-2">
                    <div className="border-r border-t border-gray-300 p-2 text-center">{val}</div>
                    <div className={`border-t border-gray-300 p-2 text-center ${SWOTService.getRiskColorClass(getRiskLevel(val, 1).level)}`}>
                      {getRiskLevel(val, 1).display}
                    </div>
                  </div>
                ))}
                <div className="grid grid-cols-2">
                  <div className="border-r border-t border-gray-300 p-2 text-center">20 à 25</div>
                  <div className="border-t border-gray-300 p-2 text-center bg-red-200">Élevé</div>
                </div>
              </div>
            </div>

            {/* Risk Matrix */}
            <div>
              <h4 className="font-semibold mb-3">Évaluation de Risque</h4>
              <div className="border border-gray-300">
                <div className="grid grid-cols-6">
                  <div className="border-r border-b border-gray-300 p-2 bg-gray-100"></div>
                  <div className="border-r border-b border-gray-300 p-2 text-center bg-gray-100 font-semibold">1</div>
                  <div className="border-r border-b border-gray-300 p-2 text-center bg-gray-100 font-semibold">2</div>
                  <div className="border-r border-b border-gray-300 p-2 text-center bg-gray-100 font-semibold">3</div>
                  <div className="border-r border-b border-gray-300 p-2 text-center bg-gray-100 font-semibold">4</div>
                  <div className="border-b border-gray-300 p-2 text-center bg-gray-100 font-semibold">5</div>
                </div>
                {[1,2,3,4,5].map(prob => (
                  <div key={prob} className="grid grid-cols-6">
                    <div className="border-r border-b border-gray-300 p-2 text-center bg-gray-100 font-semibold">{prob}</div>
                    {[1,2,3,4,5].map(impact => {
                      const risk = getRiskLevel(prob, impact);
                      return (
                        <div key={impact} className={`border-r border-b border-gray-300 p-2 text-center ${SWOTService.getRiskColorClass(risk.level)}`}>
                          {prob * impact}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
              <div className="mt-4 grid grid-cols-3 gap-2 text-sm">
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-green-200 border mr-2"></div>
                  <span>Faible</span>
                </div>
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-yellow-200 border mr-2"></div>
                  <span>Moyen</span>
                </div>
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-red-200 border mr-2"></div>
                  <span>Élevé</span>
                </div>
              </div>
            </div>
          </div>

          {/* Threats Summary */}
          {threats.length > 0 && (
            <div className="mt-6">
              <h4 className="font-semibold mb-3">Current Threats Assessment</h4>
              <div className="grid gap-2">
                {threats.map((threat, index) => {
                  const risk = getRiskLevel(threat.probability!, threat.impact!);
                  return (
                    <div key={index} className={`p-3 rounded border ${SWOTService.getRiskColorClass(risk.level)}`}>
                      <div className="flex justify-between items-center">
                        <span className="font-medium">{threat.aspect}</span>
                        <span className="text-sm">
                          Risk: {threat.probability} × {threat.impact} = {threat.probability! * threat.impact!} ({risk.display})
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-medium text-gray-900">SWOT Analysis</h3>
          <p className="text-sm text-gray-600 mt-1">Analyze Strengths, Weaknesses, Opportunities, and Threats</p>
        </div>
        {loading && (
          <div className="text-sm text-blue-600">Loading...</div>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
          {error}
          <button 
            onClick={() => setError(null)}
            className="ml-2 text-red-500 hover:text-red-700"
          >
            ×
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex space-x-1 mb-6 border-b border-gray-200">
        {[
          { key: 'strengths', label: 'Strengths', color: 'text-green-600 border-green-500' },
          { key: 'weaknesses', label: 'Weaknesses', color: 'text-yellow-600 border-yellow-500' },
          { key: 'opportunities', label: 'Opportunities', color: 'text-blue-600 border-blue-500' },
          { key: 'threats', label: 'Threats', color: 'text-red-600 border-red-500' },
          { key: 'risk-matrix', label: 'Risk Matrix', color: 'text-gray-600 border-gray-500' }
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key as any)}
            className={`kata-readonly-allowed px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.key 
                ? tab.color 
                : 'text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="space-y-6">
        {activeTab === 'strengths' && renderSWOTTable('strengths')}
        {activeTab === 'weaknesses' && renderSWOTTable('weaknesses')}
        {activeTab === 'opportunities' && renderSWOTTable('opportunities')}
        {activeTab === 'threats' && renderSWOTTable('threats')}
        {activeTab === 'risk-matrix' && renderRiskMatrix()}
      </div>

      {/* Add/Edit Form Modal */}
      {(showAddForm || editingItem) && canEdit && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {editingItem ? 'Edit' : 'Add'} {activeTab.slice(0, -1)}
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Aspect</label>
                <input
                  type="text"
                  value={editingItem ? editingItem.aspect : newItem.aspect}
                  onChange={(e) => editingItem 
                    ? setEditingItem({...editingItem, aspect: e.target.value})
                    : setNewItem({...newItem, aspect: e.target.value})
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>

              {activeTab === 'threats' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Probabilité (1-5)</label>
                      <input
                        type="number"
                        min="1"
                        max="5"
                        value={editingItem ? editingItem.probability || '' : newItem.probability || ''}
                        onChange={(e) => {
                          const val = parseInt(e.target.value);
                          if (editingItem) {
                            setEditingItem({...editingItem, probability: val});
                          } else {
                            setNewItem({...newItem, probability: val});
                          }
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Impact (1-5)</label>
                      <input
                        type="number"
                        min="1"
                        max="5"
                        value={editingItem ? editingItem.impact || '' : newItem.impact || ''}
                        onChange={(e) => {
                          const val = parseInt(e.target.value);
                          if (editingItem) {
                            setEditingItem({...editingItem, impact: val});
                          } else {
                            setNewItem({...newItem, impact: val});
                          }
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Analyse des Causes</label>
                    <textarea
                      value={editingItem ? editingItem.cause_analysis || '' : newItem.cause_analysis || ''}
                      onChange={(e) => editingItem 
                        ? setEditingItem({...editingItem, cause_analysis: e.target.value})
                        : setNewItem({...newItem, cause_analysis: e.target.value})
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      rows={3}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Actions</label>
                    <textarea
                      value={editingItem ? editingItem.actions || '' : newItem.actions || ''}
                      onChange={(e) => editingItem 
                        ? setEditingItem({...editingItem, actions: e.target.value})
                        : setNewItem({...newItem, actions: e.target.value})
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      rows={3}
                    />
                  </div>
                </>
              )}

              {activeTab === 'weaknesses' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Analyse des Causes</label>
                  <textarea
                    value={editingItem ? editingItem.cause_analysis || '' : newItem.cause_analysis || ''}
                    onChange={(e) => editingItem 
                      ? setEditingItem({...editingItem, cause_analysis: e.target.value})
                      : setNewItem({...newItem, cause_analysis: e.target.value})
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    rows={3}
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {activeTab === 'strengths' ? 'Actions de Maintien' : 
                   activeTab === 'threats' ? '' : 'Actions'}
                </label>
                {activeTab !== 'threats' && (
                  <textarea
                    value={editingItem ? editingItem.description : newItem.description}
                    onChange={(e) => editingItem 
                      ? setEditingItem({...editingItem, description: e.target.value})
                      : setNewItem({...newItem, description: e.target.value})
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    rows={3}
                  />
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Responsable</label>
                  <input
                    type="text"
                    value={editingItem ? editingItem.responsible || '' : newItem.responsible}
                    onChange={(e) => editingItem 
                      ? setEditingItem({...editingItem, responsible: e.target.value})
                      : setNewItem({...newItem, responsible: e.target.value})
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                  <input
                    type="date"
                    value={editingItem ? editingItem.date || '' : newItem.date}
                    onChange={(e) => editingItem 
                      ? setEditingItem({...editingItem, date: e.target.value})
                      : setNewItem({...newItem, date: e.target.value})
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Effectiveness of the action</label>
                <input
                  type="text"
                  value={editingItem ? editingItem.effectiveness || '' : newItem.effectiveness}
                  onChange={(e) => editingItem 
                    ? setEditingItem({...editingItem, effectiveness: e.target.value})
                    : setNewItem({...newItem, effectiveness: e.target.value})
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowAddForm(false);
                  setEditingItem(null);
                }}
                className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (editingItem) {
                    handleEditItem(editingItem);
                  } else {
                    handleAddItem();
                  }
                }}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                disabled={loading}
              >
                {editingItem ? 'Update' : 'Add'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SWOTAnalysisSection;
